/**
 * DragSelectWrapper Component
 *
 * Custom drag-to-select implementation for the timeslot grid.
 * Uses a DOMVector approach for proper coordinate handling in all directions.
 *
 * Key insight: We track ALL elements that have EVER been intersected during
 * the drag, not just elements currently intersecting. This prevents the
 * "deselect when passing through" bug.
 *
 * @requirements 3.1, 3.6, 3.7, 5.5
 */

import React, { useCallback, useRef, useState, useEffect } from 'react'
import styled from 'styled-components'
import { COLORS } from '../utils/theme'

export const DRAG_THRESHOLD_PX = 10

export interface DragSelectWrapperProps {
  children: React.ReactNode
  onSelectionStart: () => void
  onSelectionChange: (box: DOMRect | null, elements: Element[]) => void
  onSelectionEnd: (box: DOMRect | null, elements: Element[]) => void
  enabled: boolean
}

const Container = styled.div`
  position: relative;
  width: 100%;
`

/**
 * DOMVector class for representing drag operations with direction and magnitude.
 * Unlike DOMRect (which has non-negative width/height), DOMVector preserves
 * the direction of the drag, making coordinate math much simpler.
 */
class DOMVector {
  x: number

  y: number

  magnitudeX: number

  magnitudeY: number

  constructor(x: number, y: number, magnitudeX: number, magnitudeY: number) {
    this.x = x
    this.y = y
    this.magnitudeX = magnitudeX
    this.magnitudeY = magnitudeY
  }

  toDOMRect(): DOMRect {
    return new DOMRect(
      Math.min(this.x, this.x + this.magnitudeX),
      Math.min(this.y, this.y + this.magnitudeY),
      Math.abs(this.magnitudeX),
      Math.abs(this.magnitudeY)
    )
  }

  getDiagonalLength(): number {
    return Math.sqrt(this.magnitudeX ** 2 + this.magnitudeY ** 2)
  }
}

/**
 * Check if two DOMRects intersect
 */
function rectsIntersect(rect1: DOMRect, rect2: DOMRect): boolean {
  if (rect1.right < rect2.left || rect2.right < rect1.left) return false
  if (rect1.bottom < rect2.top || rect2.bottom < rect1.top) return false
  return true
}

const DragSelectWrapper: React.FC<DragSelectWrapperProps> = ({
  children,
  onSelectionStart,
  onSelectionChange,
  onSelectionEnd,
  enabled,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [dragVector, setDragVector] = useState<DOMVector | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  // Track ALL elements that have been intersected during this drag operation
  const intersectedElementsRef = useRef<Set<Element>>(new Set())
  // Track the pointer ID for releasing capture
  const pointerIdRef = useRef<number | null>(null)

  const selectionRect = dragVector && isDragging ? dragVector.toDOMRect() : null

  /**
   * Update the set of intersected elements.
   * Key: We ADD to the set, never remove during a drag operation.
   */
  const updateIntersectedElements = useCallback(
    (vector: DOMVector) => {
      if (!containerRef.current) return

      const containerRect = containerRef.current.getBoundingClientRect()
      const selectionDOMRect = vector.toDOMRect()

      containerRef.current.querySelectorAll('[data-slot-id]').forEach((el) => {
        if (!(el instanceof HTMLElement)) return

        // Check if this element should be selectable - only exclude booked slots
        const status = el.getAttribute('data-slot-status')
        if (status === 'booked') return

        const itemRect = el.getBoundingClientRect()
        // Convert to container-relative coordinates
        const translatedItemRect = new DOMRect(
          itemRect.x - containerRect.x,
          itemRect.y - containerRect.y,
          itemRect.width,
          itemRect.height
        )

        if (rectsIntersect(selectionDOMRect, translatedItemRect)) {
          // ADD to the set - never remove during drag
          intersectedElementsRef.current.add(el)
        }
      })

      // Notify with all intersected elements
      onSelectionChange(vector.toDOMRect(), Array.from(intersectedElementsRef.current))
    },
    [onSelectionChange]
  )

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!enabled || e.button !== 0) return

      // Check if we should start selecting - only prevent on booked slots
      const target = e.target as Element
      if (target.closest('[data-slot-status="booked"]')) return

      const containerRect = e.currentTarget.getBoundingClientRect()

      // Initialize drag vector at pointer position
      setDragVector(
        new DOMVector(e.clientX - containerRect.x, e.clientY - containerRect.y, 0, 0)
      )

      // Store pointer ID but DON'T capture yet - wait until drag threshold is met
      // This allows clicks to pass through to child elements
      pointerIdRef.current = e.pointerId
    },
    [enabled]
  )

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (dragVector == null) return

      const containerRect = e.currentTarget.getBoundingClientRect()

      const nextDragVector = new DOMVector(
        dragVector.x,
        dragVector.y,
        e.clientX - containerRect.x - dragVector.x,
        e.clientY - containerRect.y - dragVector.y
      )

      // Only start dragging after threshold is met
      if (!isDragging && nextDragVector.getDiagonalLength() < DRAG_THRESHOLD_PX) {
        return
      }

      if (!isDragging) {
        setIsDragging(true)
        // NOW capture the pointer since we're actually dragging
        if (pointerIdRef.current !== null) {
          e.currentTarget.setPointerCapture(pointerIdRef.current)
        }
        // Clear previous intersections when starting a new drag
        intersectedElementsRef.current.clear()
        onSelectionStart()
      }

      setDragVector(nextDragVector)
      updateIntersectedElements(nextDragVector)
    },
    [dragVector, isDragging, onSelectionStart, updateIntersectedElements]
  )

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // Release pointer capture if we have it
      if (pointerIdRef.current !== null && isDragging) {
        try {
          e.currentTarget.releasePointerCapture(pointerIdRef.current)
        } catch {
          // Ignore - pointer may already be released
        }
      }

      if (isDragging) {
        // End drag with all intersected elements
        onSelectionEnd(dragVector?.toDOMRect() ?? null, Array.from(intersectedElementsRef.current))
      }

      // Reset state
      setDragVector(null)
      setIsDragging(false)
      pointerIdRef.current = null
      intersectedElementsRef.current.clear()
    },
    [isDragging, dragVector, onSelectionEnd]
  )

  // Handle pointer leaving the window
  useEffect(() => {
    const handleBlur = () => {
      if (isDragging) {
        // Reset state on blur
        setDragVector(null)
        setIsDragging(false)
        pointerIdRef.current = null
        intersectedElementsRef.current.clear()
      }
    }

    window.addEventListener('blur', handleBlur)
    return () => window.removeEventListener('blur', handleBlur)
  }, [isDragging])

  return (
    <Container
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      {children}
      {selectionRect && (
        <div
          style={{
            position: 'absolute',
            top: selectionRect.y,
            left: selectionRect.x,
            width: selectionRect.width,
            height: selectionRect.height,
            backgroundColor: 'rgba(235, 124, 12, 0.25)',
            border: `2px solid ${COLORS.primary}`,
            borderRadius: '4px',
            zIndex: 9999,
            pointerEvents: 'none',
          }}
        />
      )}
    </Container>
  )
}

DragSelectWrapper.defaultProps = {}

export default DragSelectWrapper
